import { h, Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { formatMoney } from 'utils/storefront/format-resource';

function arrayToDictionary(arr, key) {
  const dictionary = {};
  arr.forEach((obj) => {
    if (Object.hasOwn(obj, key)) {
      dictionary[obj[key]] = obj;
    }
  });
  return dictionary;
}

function findLargestNumber(arr) {
  if (arr.length === 0) {
    return 0;
  }
  return Math.max(...arr);
}

function amountToNextGreaterValue(amount, numbers) {
  numbers.sort((a, b) => a - b);
  const nextGreaterValue = numbers.find((num) => num > amount);
  const needed = nextGreaterValue !== undefined ? nextGreaterValue - amount : 0;
  return { needed, nextGreaterValue: parseInt(nextGreaterValue, 10) || null };
}

function getShippingPromoMessage({ fulfilled, numberOfPromos, promosPerRegion, amountNeededResult }) {
  let nextRewardMessage;
  if (!fulfilled) {
    nextRewardMessage = amountNeededResult.nextItem.message_threshold_unfulfilled.replace(
      '[[amount]]',
      formatMoney({
        amount: amountNeededResult.needed,
        currencyCode: window.TMW?.localization?.currencyCode,
      }),
    );
    return nextRewardMessage;
  }
  return numberOfPromos <= 1
    ? promosPerRegion[0].message_threshold_met
    : window.TMW.Strings.cart.thresholdReached.replace('&#39;', `'`);
}

function calculateFulfillmentPercentage(thresholds, currentValue) {
  const numThresholds = thresholds.length;
  let lastThreshold = 0;
  let nextThreshold = 0;

  for (let i = 0; i < numThresholds; i += 1) {
    const parsedThreshold = parseInt(thresholds[i], 10);
    if (currentValue < parsedThreshold) {
      nextThreshold = parsedThreshold;
      break;
    }
    lastThreshold = parsedThreshold;
  }

  if (!nextThreshold) return 0;

  const leanCurrentValue = currentValue - lastThreshold;
  const leanNextThreshold = nextThreshold - lastThreshold;

  const additionalPercentage = leanCurrentValue / leanNextThreshold;

  return additionalPercentage;
}

const ProgressBar = ({ promoThresholds, totalCartPrice, fulfilled, promoWidth, barWidth }) => {
  const [fulfilledThresholds, setFulfilledThresholds] = useState(0);

  useEffect(() => {
    let thresholds = 0;
    promoThresholds.forEach((threshold) => {
      if (threshold <= totalCartPrice) thresholds += 1;
    });
    setFulfilledThresholds(thresholds);
  }, [promoThresholds, totalCartPrice]);

  const additionalPercentage = calculateFulfillmentPercentage(promoThresholds, totalCartPrice);

  const promoWidthPercentage = (promoWidth / barWidth) * 100;

  return (
    <div className="w-full h-1.5 bg-[#E2E2E0] rounded-sm">
      <div
        style={{
          width: `${
            fulfilled ? 100 : fulfilledThresholds * promoWidthPercentage + promoWidthPercentage * additionalPercentage
          }%`,
        }}
        className="bg-pantone-4 rounded-sm h-full"
        role="presentation"
      ></div>
    </div>
  );
};

const PromoIcon = ({ promoItem, totalCartPrice, numberOfPromos, isFreeShippingThreshold, position }) => {
  if (numberOfPromos <= 1 || !promoItem || !Object.keys(promoItem).length) return null;

  const { icon_threshold_met: iconMet, icon_threshold_unfulfilled: iconUnfulfilled } = promoItem;
  const itemFulfilled = totalCartPrice >= promoItem.price_threshold;

  const containerClassName = `
    absolute 
    w-[5%] 
    m-auto 
    rounded-full 
    border 
    border-[#89a290] 
    p-[2px] 
    bg-white 
    transform 
    translate-x-[-50%]
    z-2
  `;

  const textClassName = `
    absolute 
    w-[9%] 
    m-auto 
    bg-white 
    transform 
    translate-x-[-50%] 
    mt-[10%] 
    whitespace-nowrap 
    flex 
    justify-center 
    items-center
  `;

  return (
    <Fragment>
      <div className={containerClassName} style={{ left: `${position}%` }}>
        <img src={itemFulfilled ? iconMet : iconUnfulfilled} alt="Shopify Image" />
      </div>
      <div className={textClassName} style={{ left: `${position}%` }}>
        {isFreeShippingThreshold ? 'Free Shipping' : 'Free Gift'}
      </div>
    </Fragment>
  );
};

const FreeShipping = ({
  freeShippingThreshold,
  freeShippingIconThresholdMet,
  freeShippingIconThresholdUnfulfilled,
  freeShippingMessageThresholdMet,
  freeShippingMessageThresholdUnfulfilled,
  cart,
  promosWithThresholds,
  customerMarket,
}) => {
  if (!cart || !cart.state.total_price) return false;

  const [promoWidth, setPromoWidth] = useState(null);
  const [barWidth, setBarWidth] = useState(null);

  const totalCartPrice = Number(cart.state.total_price) / 100;

  const promosPerRegion = promosWithThresholds.filter(
    (promo) =>
      promo.market.toLowerCase() === customerMarket?.toLowerCase() || promo.market.toLowerCase() === 'international',
  );

  if (freeShippingThreshold) {
    promosPerRegion.push({
      icon_threshold_met: freeShippingIconThresholdMet,
      icon_threshold_unfulfilled: freeShippingIconThresholdUnfulfilled,
      message_threshold_met: freeShippingMessageThresholdMet,
      message_threshold_unfulfilled: freeShippingMessageThresholdUnfulfilled,
      price_threshold: freeShippingThreshold,
      promo_name: 'Free Shipping',
      isFreeShippingThreshold: true,
    });
  }

  const promosDictionary = arrayToDictionary(promosPerRegion, 'price_threshold');

  const promoThresholds = Object.keys(promosDictionary);

  const amountNeededResult = amountToNextGreaterValue(totalCartPrice, promoThresholds);
  amountNeededResult.nextItem = amountNeededResult.nextGreaterValue
    ? promosDictionary[amountNeededResult.nextGreaterValue]
    : null;

  const maxThreshold = findLargestNumber(promoThresholds);

  const allPromosPercentage = (totalCartPrice / maxThreshold) * 100;

  const fulfilled = totalCartPrice >= maxThreshold;

  const numberOfPromos = promoThresholds.length;

  const message = getShippingPromoMessage({
    fulfilled,
    numberOfPromos,
    promosPerRegion,
    amountNeededResult,
  });

  const barContainerRef = useRef();

  useEffect(() => {
    if (barContainerRef.current) {
      setBarWidth(barContainerRef.current.offsetWidth);
      setPromoWidth(barContainerRef.current.offsetWidth / (numberOfPromos <= 1 ? numberOfPromos : numberOfPromos + 1));
    }
  }, []);

  return (
    <div className="p-5 pt-3">
      <p data-shipping-progress-message className="text-body-2 mb-2">
        {message}
      </p>
      <div ref={barContainerRef} className="relative w-full h-full">
        <ProgressBar
          progress={allPromosPercentage}
          numberOfPromos={promosPerRegion.length}
          promoThresholds={promoThresholds}
          totalCartPrice={totalCartPrice}
          fulfilled={fulfilled}
          promoWidth={promoWidth}
          barWidth={barWidth}
        />
        <div className="flex items-center absolute top-0 left-0 w-full h-full z-2">
          {promosPerRegion
            .sort((a, b) => a.price_threshold - b.price_threshold)
            .map((promo, index) => (
              <PromoIcon
                key={index}
                promoItem={promo}
                numberOfPromos={promosPerRegion.length}
                maxThreshold={maxThreshold}
                totalCartPrice={totalCartPrice}
                isFreeShippingThreshold={promo.isFreeShippingThreshold}
                position={(((index + 1) * promoWidth) / barWidth) * 100}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default FreeShipping;
